import { useEffect, useState } from 'react';
import { speak } from './TranslatableText';

// ─────────────────────────────────────────────────────────────────────────────
// A one-question quiz in the ocean: a vocabulary chest ("what does this word
// mean?") or a temple gate ("what does the inscription say?"). The prompt is in
// the class's language, with no tap-to-translate: reading it is the point.
// ─────────────────────────────────────────────────────────────────────────────

type Props = {
  icon: string;
  title: string;
  /** What to read: a word, or an inscription. */
  prompt: string;
  lang: string;
  question: string;
  options: string[];
  /** Called with the choice; resolve true if it was right (for the gate's try-again). */
  onPick: (option: string) => Promise<boolean | void> | void;
  onClose: () => void;
  /** A wrong answer locks the buttons this long (the gate), or ends it (the chest: 0). */
  retryAfter?: number;
};

export default function OceanQuiz({ icon, title, prompt, lang, question, options, onPick, onClose, retryAfter = 0 }: Props) {
  const [busy, setBusy] = useState(false);
  const [wrong, setWrong] = useState<string | null>(null);
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return;
    const id = window.setTimeout(() => setWait(w => w - 1), 1000);
    return () => window.clearTimeout(id);
  }, [wait]);

  const pick = async (o: string) => {
    setBusy(true);
    try {
      const ok = await onPick(o);
      if (ok === false && retryAfter > 0) { setWrong(o); setWait(retryAfter); }
    } finally { setBusy(false); }
  };

  return (
    <div className="ocean-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="ocean-card quiz-card">
        <div className="row">
          <h3>{icon} {title}</h3>
          <span className="spacer" />
          <button type="button" className="icon" aria-label="Close" onClick={onClose}>✕</button>
        </div>
        <div className="quiz-prompt" dir="auto">
          <span>{prompt}</span>
          <button type="button" className="icon" aria-label="Hear it" onClick={() => speak(prompt, lang)}>🔊</button>
        </div>
        <p className="small muted" style={{ margin: 0 }}>{question}</p>
        <div className="quiz-options">
          {options.map(o => (
            <button key={o} type="button" className={wrong === o ? 'wrong' : ''} disabled={busy || wait > 0} onClick={() => void pick(o)}>{o}</button>
          ))}
        </div>
        {wait > 0 && <p className="small" style={{ margin: 0 }}>Not quite. Read it again… you can try in {wait}s.</p>}
      </div>
    </div>
  );
}
